import { useEffect, useMemo, useState } from 'react';

import { getSectionColor, SECTION_LABEL_BY_KEY } from './sectionColors';
import styles from './TimingThumbnail.module.css';
import { useDraggablePosition } from './useDraggablePosition';

interface TimingThumbnailProps {
  timings: Record<string, number>;
}

interface WebVitals {
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
}

/**
 * Server-side phase split (NGF-6527 follow-up): the wall-clock DATA phase (fetches + getProps) vs the
 * React island RENDER/serialize phase — the latter invisible to the OC-TracedTime `timings` above,
 * because it happens after the frontmatter/loader returns and after that header is flushed. The server
 * knows these only AFTER the whole response (this thumbnail included) has rendered, so it can't be a
 * prop; instead the server injects `window.__OC_PHASE_TIMINGS__` into the buffered HTML when tracing
 * (RR7 atlas server.ts and atlas-astro middleware/server.ts both inject it), and this panel reads it
 * client-side on mount. Absent → the "Server phases" group simply doesn't render (e.g. an untraced
 * request, which sends no `OC-TraceTiming` header, so the server never buffers or injects).
 */
interface ServerPhases {
  dataMs: number;
  renderMs: number;
  totalMs: number;
}

declare global {
  interface Window {
    __OC_PHASE_TIMINGS__?: ServerPhases;
  }
}

interface ComponentRow {
  ms: number;
  section: string;
  type: string;
}

const LOAD_AND_COMPOSE_KEY = 'loadAndComposeSectionComponents';

function parseTimings(timings: Record<string, number>): {
  componentRows: ComponentRow[];
  componentsSubtotal: number | undefined;
  serverFetches: Array<{ ms: number; name: string }>;
} {
  const serverFetches: Array<{ ms: number; name: string }> = [];
  const componentRows: ComponentRow[] = [];
  let componentsSubtotal: number | undefined;

  for (const [key, ms] of Object.entries(timings)) {
    if (key === LOAD_AND_COMPOSE_KEY) {
      componentsSubtotal = ms;
    } else if (key.startsWith('component:')) {
      // Format: component:<sectionKey>:<TYPE>
      const parts = key.split(':');
      const sectionKey = parts[1] ?? '';
      const type = parts.slice(2).join(':');
      componentRows.push({ section: SECTION_LABEL_BY_KEY[sectionKey] ?? sectionKey, type, ms });
    } else {
      serverFetches.push({ name: key, ms });
    }
  }

  serverFetches.sort((a, b) => b.ms - a.ms);
  componentRows.sort((a, b) => b.ms - a.ms);

  return { serverFetches, componentRows, componentsSubtotal };
}

function measureWebVitals(): Promise<WebVitals> {
  return new Promise(resolve => {
    if (typeof performance === 'undefined') {
      resolve({ ttfb: null, fcp: null, lcp: null });
      return;
    }

    // TTFB from PerformanceNavigationTiming
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const ttfb = navEntries.length > 0 ? Math.round(navEntries[0].responseStart) : null;

    // FCP from paint entries
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries.find(e => e.name === 'first-contentful-paint');
    const fcp = fcpEntry ? Math.round(fcpEntry.startTime) : null;

    // LCP via PerformanceObserver (buffered)
    let lcp: number | null = null;
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          lcp = Math.round(entries[entries.length - 1].startTime);
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      // Give LCP observer a tick to process buffered entries, then resolve
      setTimeout(() => {
        observer.disconnect();
        resolve({ ttfb, fcp, lcp });
      }, 0);
    } catch {
      resolve({ ttfb, fcp, lcp });
    }
  });
}

export function TimingThumbnail({ timings }: TimingThumbnailProps) {
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [vitals, setVitals] = useState<WebVitals>({ ttfb: null, fcp: null, lcp: null });
  // Read client-side only (never SSR) so an injected value can't cause a hydration mismatch.
  const [phases, setPhases] = useState<ServerPhases | null>(null);
  const { containerRef, dragHandlers, style, wasDragged } = useDraggablePosition('timingThumbnail:pos');

  useEffect(() => {
    setMounted(true);
    setPhases(window.__OC_PHASE_TIMINGS__ ?? null);
    let cancelled = false;
    void measureWebVitals().then(v => {
      if (!cancelled) setVitals(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // timings comes from loader data and never changes for the component's lifetime,
  // so avoid rerunning the sort/split work on the frequent re-renders during drag.
  const { componentRows, componentsSubtotal, serverFetches } = useMemo(() => parseTimings(timings), [timings]);

  if (!mounted) return null;

  const maxServerMs = serverFetches.length > 0 ? (serverFetches[0].ms ?? 1) : 1;

  return (
    <div className={styles.thumbnail} ref={containerRef} style={style}>
      <button
        className={styles.header}
        onClick={() => {
          if (wasDragged()) return;
          setCollapsed(c => !c);
        }}
        title="Drag to move. Click to collapse. Server timings and Web Vitals — send OC-TraceTiming: true request header to populate."
        type="button"
        {...dragHandlers}
      >
        <span className={styles.title}>Timing</span>
        <span className={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          {/* Web Vitals */}
          <div className={styles.group}>
            <div className={styles.groupHeader}>
              <span className={styles.groupLabel}>Web Vitals</span>
            </div>
            <div className={styles.rows}>
              {(['TTFB', 'FCP', 'LCP'] as const).map(name => {
                const ms = name === 'TTFB' ? vitals.ttfb : name === 'FCP' ? vitals.fcp : vitals.lcp;
                return (
                  <div className={styles.vitalRow} key={name}>
                    <span className={styles.vitalName}>{name}</span>
                    {ms !== null ? (
                      <span className={styles.vitalMs}>{ms} ms</span>
                    ) : (
                      <span className={styles.vitalPending}>pending</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Server phases (NGF-6527 follow-up): the data-vs-render/serialize split. Only shown when the
              server injected window.__OC_PHASE_TIMINGS__ (i.e. the framework buffered the traced
              response to measure render). `renderMs` is the phase the Server-fetches list below can't
              see. Reuses the Web-Vitals row styles (name + ms, no bar). */}
          {phases && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>
                <span className={styles.groupLabel}>Server phases</span>
                <span className={styles.groupTotal}>{phases.totalMs} ms total</span>
              </div>
              <div className={styles.rows}>
                {(
                  [
                    ['Data', phases.dataMs],
                    ['Render/serialize', phases.renderMs],
                    ['Total', phases.totalMs],
                  ] as const
                ).map(([name, ms]) => (
                  <div className={styles.vitalRow} key={name}>
                    <span className={styles.vitalName}>{name}</span>
                    <span className={styles.vitalMs}>{ms} ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Server fetches */}
          {serverFetches.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>
                <span className={styles.groupLabel}>Server fetches</span>
              </div>
              <div className={styles.rows}>
                {serverFetches.map(({ ms, name }) => {
                  const pct = Math.max(4, Math.round((ms / maxServerMs) * 100));
                  return (
                    <div className={styles.row} key={name}>
                      <span className={styles.rowName}>{name}</span>
                      <div className={styles.barTrack}>
                        <div className={styles.barFill} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={styles.rowMs}>{ms} ms</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Components */}
          {(componentRows.length > 0 || componentsSubtotal !== undefined) && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>
                <span className={styles.groupLabel}>Components</span>
                {componentsSubtotal !== undefined && (
                  <span className={styles.groupTotal}>{componentsSubtotal} ms total</span>
                )}
              </div>
              <div className={styles.rows}>
                {componentRows.map(({ ms, section, type }) => {
                  const color = getSectionColor(section);
                  return (
                    <div
                      className={styles.row}
                      key={`${section}:${type}`}
                      style={{ borderLeftColor: color, borderLeftWidth: 3 }}
                    >
                      <span className={styles.rowName} title={`${section} — ${type}`}>
                        {type}
                      </span>
                      <span className={styles.rowMs}>{ms} ms</span>
                    </div>
                  );
                })}
                {componentsSubtotal !== undefined && (
                  <div className={styles.subtotalRow}>
                    <span className={styles.subtotalLabel}>loadAndCompose subtotal</span>
                    <span className={styles.subtotalMs}>{componentsSubtotal} ms</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
